import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import type { components } from "@/shared/api";
import { cn } from "@/shared/lib";

import { formatDuration } from "../../lib/formatDuration";
import { assetName } from "../../lib/assetName";
import { isNormalGame } from "../../lib/isNormalGame";
import { HeroAvatar } from "../HeroAvatar";
import { GameModeLabel } from "./GameModeLabel";
import { MatchAnalysisAction } from "./MatchAnalysisAction";
import { ModeLabel } from "./ModeLabel";
import { NoAnalysisLabel } from "./NoAnalysisLabel";
import { StartAnalysisError } from "./StartAnalysisError";
import { MatchPlaceBadge } from "../MatchPlaceBadge";
import { MatchScoreBadge } from "../MatchScoreBadge";

type MatchSummary = components["schemas"]["MatchSummary"];
type Hero = components["schemas"]["HeroOut"];

type MatchRowProps = {
  match: MatchSummary;
  hero: Hero | undefined;
  isStarting: boolean;
  startDisabled: boolean;
  // Ошибка запуска разбора этого матча (лимит, матч не найден) — под строкой, у кнопки.
  startError?: unknown;
  onStart: (matchId: number) => void;
  // Метка рядом с местом в матче (значок хайлайтов матча).
  badge?: ReactNode;
};

// Строка списка: герой, исход, место в матче (MVP, 2-й, 3-й) и метка хайлайтов, дата,
// длительность и тип матча, быстрая оценка, K/D/A, разбор.
// Street Brawl и другие режимы — с меткой режима и без «Разобрать»: разбор только для 6 на 6.
export const MatchRow = ({
  match,
  hero,
  isStarting,
  startDisabled,
  startError = null,
  onStart,
  badge,
}: MatchRowProps) => {
  const { i18n, t } = useLingui();
  const heroId = match.hero_id;
  const heroName = hero === undefined ? t`Hero #${heroId}` : assetName(hero.name);
  const started = new Date(match.started_at);
  // Год — только у матчей прошлых лет: история может быть за несколько сезонов.
  const startedAt = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    year: started.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(started);
  const { kills, deaths, assists } = match;

  return (
    <li
      data-match-id={match.match_id}
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border-l-4 bg-surface-raised px-3 py-2",
        match.won ? "border-good" : "border-bad",
      )}
    >
      <HeroAvatar name={heroName} iconUrl={hero?.icon_url ?? null} />
      <span className="flex min-w-0 grow flex-col">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-semibold">{heroName}</span>
          <span className={cn("text-xs font-medium", match.won ? "text-good" : "text-bad")}>
            {match.won ? <Trans>victory</Trans> : <Trans>defeat</Trans>}
          </span>
          <MatchPlaceBadge rank={match.mvp_rank} />
          {badge}
        </span>
        <span className="text-xs text-subtle">
          <time dateTime={match.started_at}>{startedAt}</time> · {formatDuration(match.duration_s)}
          {match.game_mode !== null && match.game_mode !== "normal" ? (
            <>
              {" · "}
              <span className="text-warn">
                <GameModeLabel mode={match.game_mode} />
              </span>
            </>
          ) : (
            <ModeLabel mode={match.match_mode} />
          )}
        </span>
      </span>
      <MatchScoreBadge score={match.score} />
      {/* Ширины столбцов фиксированы, чтобы K/D/A и действие стояли ровно во всех строках:
          13ch вмещают «99 / 99 / 100», 10rem — самую длинную подпись действия. */}
      <span className="w-[13ch] shrink-0 text-right font-mono text-sm whitespace-nowrap text-foreground-soft">
        <span className="sr-only">
          <Trans>
            Kills {kills}, deaths {deaths}, assists {assists}
          </Trans>
        </span>
        <span aria-hidden="true">
          {kills} / {deaths} / {assists}
        </span>
      </span>
      <span className="flex w-40 shrink-0 justify-end">
        {isNormalGame(match) || match.analysis_id !== null ? (
          <MatchAnalysisAction
            matchId={match.match_id}
            hasAnalysis={match.analysis_id !== null}
            analysisStatus={match.analysis_status}
            isStarting={isStarting}
            disabled={startDisabled}
            onStart={onStart}
          />
        ) : (
          <NoAnalysisLabel />
        )}
      </span>
      {startError !== null && (
        <div className="w-full">
          <StartAnalysisError error={startError} />
        </div>
      )}
    </li>
  );
};
